import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from '@/ui/icons'
import { Badge, StatusBadge } from '@/ui/design-system'
import type { CampspotterAssessment } from '@/shared/api'

export const DEFAULT_EMPTY_MESSAGE =
  'Add campsites or meetup locations with coordinates and set trip dates, then get weather.'

interface CampspotterWeatherCardProps {
  assessments: CampspotterAssessment[]
  isLoading?: boolean
  error?: string | null
  emptyMessage?: string
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Great'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Fair'
  return 'Challenging'
}

/** Map score to design-system status for consistent badge styling */
function getScoreStatus(
  score: number
): 'success' | 'info' | 'warning' | 'error' {
  if (score >= 80) return 'success'
  if (score >= 60) return 'info'
  if (score >= 40) return 'warning'
  return 'error'
}

const CAMPSPOTTER_CREDIT = (
  <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-earth-200 shrink-0">
    Powered by{' '}
    <a
      href="https://campspotter.io"
      target="_blank"
      rel="noopener noreferrer"
      className="text-adventure-600 hover:text-adventure-700 underline"
    >
      Campspotter
    </a>
  </p>
)

/** Min/max height for the scrollable spot content so layout stays fixed and doesn't push the page. */
const SPOT_CONTENT_MIN_HEIGHT = 200
const SPOT_CONTENT_MAX_HEIGHT = 380

function SpotCard({ assessment }: { assessment: CampspotterAssessment }) {
  const score = Math.round(assessment.overallScore)
  const scoreLabel = getScoreLabel(score)
  const scoreStatus = getScoreStatus(score)
  return (
    <div className="rounded-xl border border-earth-200 bg-earth-50/50 p-4 shadow-sm">
      {/* Location header with score */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-earth-900">
            {assessment.spotName || 'Unknown spot'}
          </h3>
          {(assessment.nearestCity || assessment.startDate) && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {[assessment.nearestCity, `${assessment.startDate} – ${assessment.endDate}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-earth-200 bg-earth-50 text-base font-semibold text-earth-800"
            title={`Score ${score} of 100`}
          >
            {score}
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={scoreStatus} className="text-xs">
              {scoreLabel}
            </StatusBadge>
            <span className="text-xs text-muted-foreground">
              {assessment.minScore}–{assessment.maxScore} range
            </span>
          </div>
        </div>
      </div>

      {/* Considerations (penalties) */}
      {assessment.penalties.length > 0 && (
        <div className="mt-4 rounded-lg border border-earth-200 bg-white/60 p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Things to consider
          </p>
          <ul className="space-y-2 text-sm text-earth-700">
            {assessment.penalties.map((p, i) => (
              <li key={i} className="flex gap-2">
                <Badge
                  variant="outline"
                  className="shrink-0 border-earth-200 bg-earth-50 px-2 py-0.5 text-xs font-medium capitalize text-earth-700"
                >
                  {p.condition.replace(/_/g, ' ')}
                </Badge>
                <span>{p.explanation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Daily forecast */}
      {assessment.dailyAssessments.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Day by day
          </p>
          <div className="overflow-hidden rounded-lg border border-earth-200 bg-white/60">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 border-b border-earth-200 bg-earth-100/80 px-3 py-2 text-xs font-medium text-earth-600">
              <span>Date</span>
              <span className="text-right">Score</span>
              <span className="truncate">Conditions</span>
            </div>
            {assessment.dailyAssessments.map((day) => {
              const first = day.periods[0]
              const conditions =
                first?.weatherMain || first?.weatherDescription || '—'
              const temp =
                first?.temperatureF != null ? ` ${Math.round(first.temperatureF)}°F` : ''
              return (
                <div
                  key={day.date}
                  className="grid grid-cols-[1fr_auto_1fr] gap-3 border-b border-earth-100 px-3 py-2.5 text-sm last:border-b-0"
                >
                  <span className="font-medium text-earth-800">{day.date}</span>
                  <span className="text-right tabular-nums text-earth-700">
                    {day.dayScore != null ? Math.round(day.dayScore) : '—'}
                  </span>
                  <span className="truncate text-muted-foreground">
                    {conditions}
                    {temp}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export function CampspotterWeatherCard({
  assessments,
  isLoading = false,
  error = null,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
}: CampspotterWeatherCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    setCurrentIndex(0)
  }, [assessments])

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] flex-col">
        <div className="flex flex-1 items-center gap-3">
          <div className="h-10 w-10 rounded-full animate-pulse bg-earth-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-earth-200" />
            <div className="h-3 w-48 animate-pulse rounded bg-earth-100" />
          </div>
        </div>
        <div className="mt-4 h-20 flex-1 animate-pulse rounded-lg bg-earth-100" />
        {CAMPSPOTTER_CREDIT}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col">
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
        {CAMPSPOTTER_CREDIT}
      </div>
    )
  }

  if (assessments.length === 0) {
    return (
      <div className="flex flex-col">
        <p className="text-sm leading-relaxed text-muted-foreground">{emptyMessage}</p>
        {CAMPSPOTTER_CREDIT}
      </div>
    )
  }

  const hasMultiple = assessments.length > 1
  const current = assessments[currentIndex]
  const canGoPrev = hasMultiple && currentIndex > 0
  const canGoNext = hasMultiple && currentIndex < assessments.length - 1

  return (
    <div className="flex flex-col">
      {/* Prev/Next bar and spot indicator */}
      {hasMultiple && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
            disabled={!canGoPrev}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-earth-200 bg-white text-earth-600 transition-colors hover:bg-earth-50 disabled:pointer-events-none disabled:opacity-40"
            aria-label="Previous spot"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-0 flex-1 text-center text-sm font-medium text-earth-700">
            {currentIndex + 1} of {assessments.length}
          </span>
          <button
            type="button"
            onClick={() =>
              setCurrentIndex((i) => Math.min(assessments.length - 1, i + 1))
            }
            disabled={!canGoNext}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-earth-200 bg-white text-earth-600 transition-colors hover:bg-earth-50 disabled:pointer-events-none disabled:opacity-40"
            aria-label="Next spot"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Fixed-height scrollable area for current spot */}
      <div
        className="overflow-y-auto rounded-lg"
        style={{
          minHeight: SPOT_CONTENT_MIN_HEIGHT,
          maxHeight: SPOT_CONTENT_MAX_HEIGHT,
        }}
      >
        <SpotCard assessment={current} />
      </div>

      {CAMPSPOTTER_CREDIT}
    </div>
  )
}
